import React, { useState, useEffect, useRef, useCallback } from "react";
import { FileText, Download, Loader2 } from "lucide-react";
import PremiumModal from "../ui/PremiumModal";

interface PDFPreviewModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  fetchPDF: () => Promise<any>;
  filename: string;
}

const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  fetchPDF,
  filename,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const loadPDF = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchPDF();
      const blob = new Blob([res.data], { type: "application/pdf" });
      blobRef.current = blob;
      const url = URL.createObjectURL(blob);
      setBlobUrl(url);
    } catch (err: any) {
      setError("Failed to generate PDF preview");
      console.error("PDF preview error:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchPDF]);

  useEffect(() => {
    if (open) {
      loadPDF();
    }
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
        blobRef.current = null;
      }
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      blobRef.current = null;
    }
    onClose();
  };

  const handleDownload = () => {
    if (!blobRef.current) return;
    const url = URL.createObjectURL(blobRef.current);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PremiumModal
      open={open}
      onClose={handleClose}
      title={`PDF Preview — ${title}`}
      subtitle={subtitle}
      icon={FileText}
      size="xl"
      footer={
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">{filename}</p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={handleDownload}
              disabled={!blobUrl}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-xl transition-colors disabled:opacity-50 bg-brand hover:bg-brand-hover"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </button>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-brand mb-4" />
          <p className="text-sm text-slate-500">Generating PDF...</p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20">
          <FileText className="h-12 w-12 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-red-600">{error}</p>
          <button
            onClick={loadPDF}
            className="mt-3 text-sm text-brand hover:underline"
          >
            Try again
          </button>
        </div>
      ) : blobUrl ? (
        <iframe
          src={blobUrl}
          title="PDF Preview"
          className="w-full rounded-lg border border-slate-200"
          style={{ height: "70vh" }}
        />
      ) : null}
    </PremiumModal>
  );
};

export default PDFPreviewModal;
